import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Guideline_Role_Enum,
    Guideline_Status_Enum,
    Guideline_Type_Enum,
    GuidelineFragment,
    Guidelines_Insert_Input,
    UpdateGuidelineMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    OperationsAdminVenueAreaGuidelineCreateFragment, 
    OperationsAdminVenueAreaGuidelineCreateSchema, 
    OperationsAdminVenueAreaGuidelineUpdateFragment, 
    OperationsAdminVenueAreaGuidelineUpdateSchema 
} from "src/components/operations/administrator/areas/guidelines/OperationsAdminVenueAreaGuidelineSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = OperationsAdminVenueAreaGuidelineCreateFragment

let initialState: FormPayloadType = {
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,
};

export const CreateOperationsAdminVenueAreaGuidelineDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().guidelines;
    const { loading, errors } = useAppState().guidelines;
    const { currentItemId: currentAreaId } = useAppState(state => state.areas)

    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Guidelines_Insert_Input) => {
        return createItem({ area_id: currentAreaId, type: Guideline_Type_Enum.Area, parent_guideline_type: Guideline_Type_Enum.Area, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, GuidelineFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueAreaGuidelineCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateOperationsAdminVenueAreaGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Add Guideline'
            action={CreateOperationsAdminVenueAreaGuidelineDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: OperationsAdminVenueAreaGuidelineUpdateFragment = {
    status: Guideline_Status_Enum.Active,
    role: Guideline_Role_Enum.Info,
    name: "",
    description: "",
    tenant_required: false,
    venue_required: false,
    talent_required: false,
    document_id: null,
    metadata: null,    
    target_booking_type: null,
    target_booking_status: null,
};

export const UpdateOperationsAdminVenueAreaGuidelineDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().guidelines;
    const { currentItem, loading, errors } = useAppState().guidelines;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: OperationsAdminVenueAreaGuidelineUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<OperationsAdminVenueAreaGuidelineUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateGuidelineMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<OperationsAdminVenueAreaGuidelineUpdateFragment, GuidelineFragment, string>
                autoOpen={false}
                formSchema={OperationsAdminVenueAreaGuidelineUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateOperationsAdminVenueAreaGuidelineAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().guidelines;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.guidelines.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateOperationsAdminVenueAreaGuidelineDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditOperationsAdminVenueAreaGuidelineAction = () => {
    return (
        <PageDrawerAction<GuidelineFragment>
            title='Edit Guideline'
            action={UpdateOperationsAdminVenueAreaGuidelineDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}